/**
 * Align and distribute, as arithmetic on boxes.
 *
 * Pure and store-free, like `collision.ts`: the caller turns tables into
 * `Boxed` (centre plus rotated half-extent, chairs included) and turns the
 * answer back into a move. Keeping it out of the store is what makes the
 * awkward cases — a selection too tight to distribute, an alignment that
 * changes nothing — testable without a canvas.
 *
 * Everything here works on **edges**, never centres. "Align left" on a round
 * table and a banquet table means their left edges line up; aligning their
 * centres would leave the edges as ragged as they started, which is the one
 * thing the planner pressed the button to fix.
 */

import type { Bounds } from './geometry'

export interface Boxed {
  id: string
  /** Centre, in world coordinates — the same x/y a table stores. */
  x: number
  y: number
  /** Half-extent of the axis-aligned box, chairs included. */
  halfX: number
  halfY: number
}

export interface Move {
  id: string
  x: number
  y: number
}

export type AlignEdge = 'left' | 'centerX' | 'right' | 'top' | 'centerY' | 'bottom'
export type DistributeAxis = 'x' | 'y'

/** Below this, a "move" is floating-point noise and not worth an undo entry. */
const EPSILON = 1e-6

interface Axis {
  centre: (box: Boxed) => number
  half: (box: Boxed) => number
  move: (box: Boxed, to: number) => Move
}

const AXES: Record<DistributeAxis, Axis> = {
  x: {
    centre: (box) => box.x,
    half: (box) => box.halfX,
    move: (box, to) => ({ id: box.id, x: to, y: box.y }),
  },
  y: {
    centre: (box) => box.y,
    half: (box) => box.halfY,
    move: (box, to) => ({ id: box.id, x: box.x, y: to }),
  },
}

const ALIGN_AXIS: Record<AlignEdge, DistributeAxis> = {
  left: 'x',
  centerX: 'x',
  right: 'x',
  top: 'y',
  centerY: 'y',
  bottom: 'y',
}

/** Drop the boxes that are already where they belong. */
function changed(boxes: Boxed[], axis: Axis, targets: number[]): Move[] {
  const moves: Move[] = []
  boxes.forEach((box, index) => {
    if (Math.abs(targets[index] - axis.centre(box)) < EPSILON) return
    moves.push(axis.move(box, targets[index]))
  })
  return moves
}

/**
 * Line the boxes up on one edge of the selection, or on its middle.
 *
 * The reference is the selection's own bounding box, so centring uses the
 * midpoint of what is selected rather than the average of the centres — with
 * two tables clustered left and one far right, the average would drag
 * everything towards the cluster and centre nothing.
 */
export function alignBoxes(boxes: Boxed[], edge: AlignEdge): Move[] {
  if (boxes.length < 2) return []
  const axis = AXES[ALIGN_AXIS[edge]]

  let low = Infinity
  let high = -Infinity
  for (const box of boxes) {
    low = Math.min(low, axis.centre(box) - axis.half(box))
    high = Math.max(high, axis.centre(box) + axis.half(box))
  }

  const targets = boxes.map((box) => {
    switch (edge) {
      case 'left':
      case 'top':
        return low + axis.half(box)
      case 'right':
      case 'bottom':
        return high - axis.half(box)
      default:
        return (low + high) / 2
    }
  })

  return changed(boxes, axis, targets)
}

/**
 * Spread the boxes so the daylight between them is equal.
 *
 * The two outermost stay where they are — the planner positioned the ends of
 * the row, and moving them would be a different request. Gaps are measured
 * between edges, so a banquet table and a sweetheart table end up with the same
 * aisle rather than the same centre spacing.
 *
 * When the boxes cannot fit in the span they already occupy, the gap would have
 * to go negative. It does not: the gap floors at zero and the row grows outward
 * from its own middle instead. Distribute has never made a room denser than the
 * planner left it, and it will not start by stacking furniture.
 */
export function distributeBoxes(boxes: Boxed[], along: DistributeAxis): Move[] {
  if (boxes.length < 3) return []
  const axis = AXES[along]

  const order = boxes.map((_, index) => index).sort(
    (a, b) => axis.centre(boxes[a]) - axis.centre(boxes[b]),
  )

  let low = Infinity
  let high = -Infinity
  let occupied = 0
  for (const box of boxes) {
    low = Math.min(low, axis.centre(box) - axis.half(box))
    high = Math.max(high, axis.centre(box) + axis.half(box))
    occupied += axis.half(box) * 2
  }

  const gap = Math.max(0, (high - low - occupied) / (boxes.length - 1))
  // Recentre on the old span. When the gap was not clamped this is a no-op,
  // so the pinned ends really do stay pinned.
  const width = occupied + gap * (boxes.length - 1)
  let cursor = (low + high) / 2 - width / 2

  const targets = new Array<number>(boxes.length)
  for (const index of order) {
    const half = axis.half(boxes[index])
    targets[index] = cursor + half
    cursor += half * 2 + gap
  }

  return changed(boxes, axis, targets)
}

/** The ids whose box overlaps the rectangle — a marquee's hit test. */
export function boxesInRect(boxes: Boxed[], rect: Bounds): string[] {
  return boxes
    .filter(
      (box) =>
        box.x - box.halfX <= rect.maxX &&
        box.x + box.halfX >= rect.minX &&
        box.y - box.halfY <= rect.maxY &&
        box.y + box.halfY >= rect.minY,
    )
    .map((box) => box.id)
}
